import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import classes from './style.module.scss';
import {Message} from "~/types";
import useTranslation from "~/app/hooks/useTranslation";
import DOMPurify from 'dompurify';
import LightBox from '~/app/common/LightBox';
import Icon from '~/app/common/Icon';
import {
    getAttachmentFileName,
    getAttachmentIconSrc,
    getMimeTypeForUrl,
    splitAttachmentUrls,
} from '~/messages/attachmentUtils';
import { downloadAttachment } from '~/app/common/Attachment/helper';
import FileTransferService from '~/sqlite/services/fileTransfer';
import { toast } from 'react-toastify';

export interface ListItemProps {
    item: Message
    isOwn?: boolean
    isFirstInGroup?: boolean
    isLastInGroup?: boolean
}

export type ListItemComponent = React.FC<ListItemProps>

const ListItem: ListItemComponent = ({
    item,
    isOwn = false,
    isFirstInGroup = true,
    isLastInGroup = true,
}) => {
    const {t} = useTranslation("")
    const cleanHTML = DOMPurify.sanitize(item.message || '');
    const messageLength = cleanHTML.replace(/<[^>]*>/g, '').trim().length;
    const hasText = messageLength > 0;
    const attachmentUrls = useMemo(() => item.attachmentUrls ?? [], [item.attachmentUrls]);
    const { imageUrls, documentUrls } = useMemo(
        () => splitAttachmentUrls(attachmentUrls),
        [attachmentUrls],
    );
    const isAction = !!item.actions;
    const visibleImages = imageUrls.slice(0, 4);
    const overflowCount = Math.max(0, imageUrls.length - 4);
    const layoutCount = Math.min(imageUrls.length, 4);

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState(0);
    const [openingDocUrl, setOpeningDocUrl] = useState<string | null>(null);
    const [downloadingDocUrl, setDownloadingDocUrl] = useState<string | null>(null);

    const openGallery = useCallback((index: number) => {
        setGalleryIndex(index);
        setGalleryOpen(true);
    }, []);

    const closeGallery = useCallback(() => {
        setGalleryOpen(false);
    }, []);

    const openDocument = useCallback(async (url: string) => {
        if (!url || openingDocUrl) {
            return;
        }
        const mimeType = getMimeTypeForUrl(url);
        const fileTransfer = new FileTransferService();

        if (!window['cordova']) {
            fileTransfer.openFile(url, mimeType);
            return;
        }

        setOpeningDocUrl(url);
        try {
            const existingPath = await fileTransfer.isFileExist(url, 'chat');
            if (existingPath) {
                fileTransfer.openFile(existingPath, mimeType);
                return;
            }
            const downloadedPath = await fileTransfer.downloadFileFromURL(url, 'chat');
            if (downloadedPath) {
                const absolutePath = fileTransfer.getFileNameFromURL(url, 'chat');
                fileTransfer.openFile(absolutePath, mimeType);
            } else {
                toast.error(t('issue.messages.openDocumentFailed'));
            }
        } catch {
            toast.error(t('issue.messages.openDocumentFailed'));
        } finally {
            setOpeningDocUrl(null);
        }
    }, [openingDocUrl, t]);

    const downloadDocument = useCallback(async (url: string, event: React.MouseEvent) => {
        event.stopPropagation();
        if (!url || downloadingDocUrl) {
            return;
        }
        const fileName = getAttachmentFileName(url);
        const mimeType = getMimeTypeForUrl(url);
        setDownloadingDocUrl(url);
        try {
            await downloadAttachment(url, fileName, mimeType);
            toast.success(
                t(window['cordova']?.platformId === 'ios'
                    ? 'issue.messages.downloadDocumentSuccessIos'
                    : 'issue.messages.downloadDocumentSuccess'),
            );
        } catch {
            toast.error(t('issue.messages.downloadDocumentFailed'));
        } finally {
            setDownloadingDocUrl(null);
        }
    }, [downloadingDocUrl, t]);

    const getActionMessage = (item: Message) => {
        return item.message;
    }

    return (
        <div
            className={classNames(classes['message'], {
                [classes['message-own']]: isOwn && !isAction,
                [classes['message-other']]: !isOwn && !isAction,
                [classes['message-tight']]: !isLastInGroup && !isAction,
            })}
        >
            {!!item.showDate &&
            <div className={classes['message-date']}><span>{t("date.primary", {date: item.dateTime})}</span></div>}
            {!isAction ? (
                <div className={classes['row']}>
                    <div
                        className={classNames(classes['bubble'], {
                            [classes['bubble-own']]: isOwn,
                            [classes['bubble-other']]: !isOwn,
                            [classes['bubble-first']]: isFirstInGroup,
                            [classes['bubble-last']]: isLastInGroup,
                        })}
                    >
                        {isFirstInGroup && (
                            <div className={classes['name']}>{isOwn ? t('issue.messages.you') : item.senderName}</div>
                        )}
                        {hasText && (
                            <div className={classes['text']} dangerouslySetInnerHTML={{ __html: cleanHTML }}></div>
                        )}
                        {imageUrls.length > 0 && (
                            <div
                                className={classNames(classes['images'], {
                                    [classes['images-1']]: layoutCount === 1,
                                    [classes['images-2']]: layoutCount === 2,
                                    [classes['images-3']]: layoutCount === 3,
                                    [classes['images-4']]: layoutCount === 4,
                                })}
                            >
                                {visibleImages.map((url, index) => (
                                    <button
                                        key={`${url}-${index}`}
                                        type="button"
                                        className={classes['thumbnail']}
                                        onClick={() => openGallery(index)}
                                    >
                                        <img src={url} alt="" className={classes['thumbnail-image']} />
                                        {index === 3 && overflowCount > 0 && (
                                            <span className={classes['more-overlay']}>
                                                {`+${overflowCount}`}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                        {documentUrls.length > 0 && (
                            <div className={classes['documents']}>
                                {documentUrls.map((url, index) => {
                                    const fileName = getAttachmentFileName(url);
                                    const iconSrc = getAttachmentIconSrc(url);
                                    const isOpening = openingDocUrl === url;
                                    const isDownloading = downloadingDocUrl === url;
                                    return (
                                        <div key={`${url}-${index}`} className={classes['document-row']}>
                                            <button
                                                type="button"
                                                className={classes['document-open']}
                                                onClick={() => openDocument(url)}
                                                disabled={!!openingDocUrl || !!downloadingDocUrl}
                                            >
                                                <img
                                                    src={iconSrc}
                                                    alt=""
                                                    className={classes['document-type-icon']}
                                                />
                                                <span className={classes['document-name']}>
                                                    {isOpening ? t('issue.messages.openingDocument') : fileName}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={classes['document-download']}
                                                onClick={(event) => downloadDocument(url, event)}
                                                disabled={!!openingDocUrl || !!downloadingDocUrl}
                                                aria-label={t('issue.messages.downloadDocument')}
                                            >
                                                <Icon
                                                    name={isDownloading ? 'hourglass_empty' : 'download'}
                                                    size="20"
                                                />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {isLastInGroup && item.time && (
                            <div className={classes['time']}>{item.time}</div>
                        )}
                    </div>
                </div>
            ) : (
                <div className={classes['action']}>{getActionMessage(item)}</div>
            )}
            {imageUrls.length > 0 && galleryOpen && (
                <LightBox
                    open={galleryOpen}
                    onClose={closeGallery}
                    download={true}
                    images={imageUrls}
                    initialIndex={galleryIndex}
                    imageUrl={imageUrls[galleryIndex] ?? null}
                />
            )}
        </div>
    );
};

export default ListItem
